import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";
import { DeleteGuardResult } from "../../types/deleteGuard";

export interface ContratoType {
    id: number;
    description?: string;
    sellerAddress?: string | null;
    buyerAddress?: string | null;
    buyerNationality?: string | null;
    buyerMaritalStatus?: string | null;
    buyerProfession?: string | null;
    productDescription?: string | null;
    totalValue?: number | null;
    totalValueText?: string | null;
    downPaymentValue?: number | null;
    installmentsCount?: number | null;
    installmentValue?: number | null;
    paymentDueDay?: number | null;
    contractAnnex?: string;
    class?: string;
    hiredStudents?: number;
    currentStudents?: number;
    completionDate?: string | null;
    dateOfDischarge?: string | null;
    linkSent?: boolean;
    freebies?: boolean;
    empresaId: number;
    instituicaoId: number;
    cursoId?: number[];
    vendedorId?: number;
    responsavelId?: number;
    alunoId?: number[];
    agendaId?: number[];
    status?: number;
}

export interface CreateContratoType {
    id?: number;
    description: string;
    sellerAddress?: string | null;
    buyerAddress?: string | null;
    buyerNationality?: string | null;
    buyerMaritalStatus?: string | null;
    buyerProfession?: string | null;
    productDescription?: string | null;
    totalValue?: number | null;
    totalValueText?: string | null;
    downPaymentValue?: number | null;
    installmentsCount?: number | null;
    installmentValue?: number | null;
    paymentDueDay?: number | null;
    contractAnnex?: string;
    class?: string;
    hiredStudents?: number;
    currentStudents?: number;
    completionDate?: string | null;
    dateOfDischarge?: string | null;
    linkSent?: boolean;
    freebies?: boolean;
    empresaId: number;
    instituicaoId: number;
    cursoId?: number[];
    vendedorId?: number;
    responsavelId?: number;
    alunoId?: number[];
    agendaId?: number[];
    status?: number;
}

export interface UpdateContratoType {
    id: number;
    description?: string;
    sellerAddress?: string | null;
    buyerAddress?: string | null;
    buyerNationality?: string | null;
    buyerMaritalStatus?: string | null;
    buyerProfession?: string | null;
    productDescription?: string | null;
    totalValue?: number | null;
    totalValueText?: string | null;
    downPaymentValue?: number | null;
    installmentsCount?: number | null;
    installmentValue?: number | null;
    paymentDueDay?: number | null;
    contractAnnex?: string;
    class?: string;
    hiredStudents?: number;
    currentStudents?: number;
    completionDate?: string | null;
    dateOfDischarge?: string | null;
    linkSent?: boolean;
    freebies?: boolean;
    instituicaoId: number;
    cursoId?: number[];
    vendedorId?: number;
    responsavelId?: number;
    alunoId?: number[];
    agendaId?: number[];
    status?: number;
}

export interface ContratoAssinaturaSignatarioType {
    email: string;
    name: string;
    hasSigned: boolean;
    link?: string | null;
    phone?: string | null;
}

export interface ContratoAssinaturaType {
    id: number;
    contratoId: number;
    flashCnpj?: string | null;
    status: number;
    statusDescricao: string;
    mensagemErro?: string | null;
    dataEnvio?: string | null;
    dataConclusao?: string | null;
    signatarios: ContratoAssinaturaSignatarioType[];
}

export interface FlashCnpjType {
    cnpj: string;
    name: string;
}

export default function useContrato() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout, objUserTk } = useContext(AuthContext);
    const [listContrato, setListContrato] = useState<ContratoType[]>([]);
    const [pageCount, setPageCount] = useState<number>(0);
    const [initialListContrato, setInitialListContrato] = useState<ContratoType[]>([]);
    const [contrato, setContrato] = useState<ContratoType>({
        id: 0,
        description: "",
        contractAnnex: "",
        class: "",
        hiredStudents: 0,
        currentStudents: 0,
        completionDate: '',
        dateOfDischarge: '',
        linkSent: false,
        freebies: false,
        empresaId: 0,
        instituicaoId: 0,
        cursoId: [],
        vendedorId: 0,
        responsavelId: 0,
        alunoId: [],
        agendaId: [],
        status: 0,
    });

    function createContrato(createContratoData: CreateContratoType): Promise<CreateContratoType | null> {
        setLoading(true);
        const payload = {
            ...createContratoData,
            completionDate: createContratoData.completionDate ? createContratoData.completionDate : null,
            dateOfDischarge: createContratoData.dateOfDischarge ? createContratoData.dateOfDischarge : null,
        };
        console.log("createContratoData, ", payload);
        return api
            .post(`/Contrato/create`, payload)
            .then((response) => {
                const createdContrato: CreateContratoType = response.data;
                showToast("success", "Contrato criado com sucesso.");
                setLoading(false);
                return createdContrato;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao criar contrato. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao criar contrato:", error);
                return null;
            });
    }

    function getAllContratoPaged(
        pageNumber: number,
        pageSize: number,
        description?: string,
        empresaId?: number,
        status?: number,
        city?: string,
        startDate?: string,
        endDate?: string,
        idContrato?: number,
        state?: string,
        region?: string,
        isPrivate?: boolean,
        vendedorId?: number,
        somenteKit?: boolean,
        instituicaoId?: number,
    ): Promise<ContratoType[] | null> {
        setLoading(true);
        let url = `/Contrato/getAllContratoPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (idContrato) {
            url += `&ContratoId=${idContrato}`;
        }

        if (instituicaoId) {
            url += `&InstituicaoId=${instituicaoId}`;
        }

        if (description) {
            url += `&description=${description}`;
        }

        if (empresaId) {
            url += `&empresaId=${empresaId}`;
        }

        if (status !== undefined) {
            url += `&status=${status}`;
        }

        if (city) {
            url += `&city=${city}`;
        }

        if (state) {
            url += `&state=${state}`;
        }

        if (region) {
            url += `&region=${encodeURIComponent(region)}`;
        }

        if (isPrivate !== undefined) {
            url += `&isPrivate=${isPrivate}`;
        }

        if (vendedorId) {
            url += `&vendedorId=${vendedorId}`;
        }

        if (startDate) {
            url += `&startDate=${startDate}`;
        }

        if (endDate) {
            url += `&endDate=${endDate}`;
        }

        if (somenteKit) {
            url += `&somenteKit=${somenteKit}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListContrato(response.data.result);
                setInitialListContrato(response.data.result);
                setPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar contratos. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar contratos:", error);
                return null;
            });
    }

    function updateContrato(updateContratoData: UpdateContratoType): Promise<UpdateContratoType | null> {
        setLoading(true);
        const payload = {
            ...updateContratoData,
            completionDate: updateContratoData.completionDate ? updateContratoData.completionDate : null,
            dateOfDischarge: updateContratoData.dateOfDischarge ? updateContratoData.dateOfDischarge : null,
        };
        return api
            .put(`/Contrato/${updateContratoData.id}`, payload)
            .then((response) => {
                showToast("success", "Contrato atualizado com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao atualizar contrato. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao atualizar contrato:", error);
                return null;
            });
    }

    function deleteContrato(contratoId: number, force = false): Promise<DeleteGuardResult> {
        setLoading(true);
        return api
            .delete(`/Contrato/${contratoId}${force ? "?force=true" : ""}`)
            .then(() => {
                setContrato({
                    id: 0,
                    description: "",
                    contractAnnex: "",
                    class: "",
                    hiredStudents: 0,
                    currentStudents: 0,
                    completionDate: '',
                    dateOfDischarge: '',
                    linkSent: false,
                    freebies: false,
                    empresaId: 0,
                    instituicaoId: 0,
                    cursoId: [],
                    vendedorId: 0,
                    responsavelId: 0,
                    alunoId: [],
                    agendaId: [],
                    status: 0,
                });
                showToast("success", "Contrato deletado com sucesso.");
                setLoading(false);
                return { status: "ok" } as DeleteGuardResult;
            })
            .catch((error) => {
                setLoading(false);
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                    return { status: "error" } as DeleteGuardResult;
                }
                if (error?.response?.status === 409) {
                    return {
                        status: "conflict",
                        message: error.response.data?.message ?? "O contrato possui dependentes.",
                        dependentes: error.response.data?.dependentes ?? {},
                    } as DeleteGuardResult;
                }
                if (error?.response?.status === 404) {
                    return { status: "notFound" } as DeleteGuardResult;
                }
                showToast("error", "Erro ao deletar contrato. Verifique novamente mais tarde.", error);
                console.error("Erro ao deletar contrato:", error);
                return { status: "error" } as DeleteGuardResult;
            });
    }

    function downloadContratoPdf(contratoId: number, fallbackBase64?: string) {
        if (fallbackBase64 && fallbackBase64.trim().length > 0) {
            try {
                let cleanBase64 = fallbackBase64.trim();
                if (cleanBase64.startsWith("http://") || cleanBase64.startsWith("https://")) {
                    window.open(cleanBase64, "_blank");
                    return;
                }
                const commaIndex = cleanBase64.indexOf(",");
                if (commaIndex !== -1 && cleanBase64.slice(0, commaIndex).toLowerCase().includes("base64")) {
                    cleanBase64 = cleanBase64.slice(commaIndex + 1);
                }
                cleanBase64 = cleanBase64.replace(/\s+/g, "");
                const byteCharacters = atob(cleanBase64);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: "application/pdf" });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `contrato_${contratoId}.pdf`;
                document.body.appendChild(a);
                a.click();
                setTimeout(() => {
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                }, 100);
                showToast("success", "Download iniciado!");
                return;
            } catch (e) {
                console.warn("Falha ao abrir base64 local, tentando via API...", e);
            }
        }

        setLoading(true);
        return api
            .get(`/Contrato/getPdf/${contratoId}`, { responseType: "blob" })
            .then((response) => {
                if (!response || !response.data || response.data.size === 0) {
                    showToast("error", "Ainda não foi anexado nenhum documento para esse contrato.");
                    return;
                }

                const blob = new Blob([response.data], { type: "application/pdf" });
                const url = window.URL.createObjectURL(blob);

                const a = document.createElement("a");
                a.href = url;
                a.download = `contrato_${contratoId}.pdf`;
                document.body.appendChild(a);
                a.click();

                setTimeout(() => {
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                }, 100);
                showToast("success", "Download iniciado!");
            })
            .catch((error) => {
                if (error.response && error.response.status === 404) {
                    showToast("error", "Ainda não foi anexado um documento a esse contrato.");
                } else {
                    showToast("error", "Erro ao baixar contrato. Verifique novamente mais tarde.");
                }
            })
            .finally(() => setLoading(false));
    }

    function getContratoCursoGuidByCursoIdAndContratoId(cursoId: number, contratoId: number): Promise<string | null> {
        setLoading(true);
        return api
            .get(`/Contrato/getContratoCursoGuideByCursoIdAndContratoId/${cursoId}/${contratoId}`)
            .then((response) => {
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor fazer login novamente.", error);
                } else {
                    showToast("error", "Erro ao obter o GUID do contrato. Tente novamente mais tarde.", error);
                }
                setLoading(false);
                console.error("Erro ao obter o contrato curso GUID:", error);
                return null;
            });
    }

    function updateStatusContrato(empresaId: number, status: number): Promise<boolean> {
        setLoading(true);

        return api
            .post(`/Contrato/updateStatus/${empresaId}/${status}`)
            .then((response) => {
                showToast("success", "Status do contrato atualizado com sucesso.");
                setLoading(false);
                return true;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao atualizar status do contrato. Tente novamente mais tarde.", error);
                }
                setLoading(false);
                console.error("Erro ao atualizar status do contrato:", error);
                return false;
            });
    }

    function handleGetContratoByEmpresaId(empresaId: number): Promise<ContratoType[] | null> {
        setLoading(true);
        return api
            .get(`/Contrato/getContratoByEmpresaId/${empresaId}`)
            .then((response) => {
                const contrato: ContratoType[] = response.data;
                setListContrato(contrato);
                setLoading(false);
                return contrato;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao buscar informações dos contratos. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao buscar informações dos contratos:", error);
                return null;
            });
    }

    function getContratoById(id: number): Promise<ContratoType | null> {
        setLoading(true);
        return api.get(`/Contrato/getContratoById/${id}`)
            .then(response => {
                setLoading(false);
                return response.data;
            })
            .catch(error => {
                setLoading(false);
                showToast("error", "Erro ao buscar contrato por ID.");
                console.error("Erro:", error);
                return null;
            });
    }

    function suggestContratoDescriptions(term: string, limit: number = 10): Promise<string[]> {
        const q = (term || "").trim();
        if (!q) return Promise.resolve([]);

        const empresaId = objUserTk?.EmpresaId;
        if (!empresaId) return Promise.resolve([]);

        const url = `/Contrato/suggest-descriptions?empresaId=${empresaId}&q=${encodeURIComponent(q)}&limit=${limit}`;

        return api
            .get(url)
            .then((response) => {
                const items = Array.isArray(response.data) ? response.data as string[] : [];
                return items;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    console.error("Erro ao buscar sugestões de descrição:", error);
                }
                return [];
            });
    }

    function replaceCursosDoContrato(contratoId: number, cursoIds: number[]): Promise<boolean> {
        setLoading(true);
        return api
            .put(`/Contrato/${contratoId}/cursos`, { cursoIds })
            .then(() => {
                showToast("success", "Cursos do contrato atualizados com sucesso.");
                setLoading(false);
                return true;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao atualizar cursos do contrato. Tente novamente mais tarde.", error);
                }
                setLoading(false);
                console.error("Erro ao substituir cursos do contrato:", error);
                return false;
            });
    }

    function addCursoAoContrato(contratoId: number, cursoId: number): Promise<boolean> {
        setLoading(true);
        return api
            .post(`/Contrato/${contratoId}/cursos/${cursoId}`)
            .then(() => {
                showToast("success", "Curso vinculado ao contrato.");
                setLoading(false);
                return true;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao vincular curso ao contrato. Tente novamente mais tarde.", error);
                }
                setLoading(false);
                console.error("Erro ao adicionar curso ao contrato:", error);
                return false;
            });
    }

    function removeCursoDoContrato(contratoId: number, cursoId: number, force = false): Promise<DeleteGuardResult> {
        setLoading(true);
        return api
            .delete(`/Contrato/${contratoId}/cursos/${cursoId}${force ? "?force=true" : ""}`)
            .then(() => {
                showToast("success", "Curso removido do contrato.");
                setLoading(false);
                return { status: "ok" } as DeleteGuardResult;
            })
            .catch((error) => {
                setLoading(false);
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                    return { status: "error" } as DeleteGuardResult;
                }
                if (error?.response?.status === 409) {
                    return {
                        status: "conflict",
                        message: error.response.data?.message ?? "O curso possui alunos vinculados.",
                        dependentes: error.response.data?.dependentes ?? {},
                    } as DeleteGuardResult;
                }
                if (error?.response?.status === 404) {
                    return { status: "notFound" } as DeleteGuardResult;
                }
                showToast("error", "Erro ao remover curso do contrato. Tente novamente mais tarde.", error);
                console.error("Erro ao remover curso do contrato:", error);
                return { status: "error" } as DeleteGuardResult;
            });
    }

    function getFlashCnpjs(): Promise<FlashCnpjType[]> {
        return api
            .get(`/Contrato/assinatura/flash-cnpjs`)
            .then((response) => (Array.isArray(response.data) ? (response.data as FlashCnpjType[]) : []))
            .catch((error) => {
                console.error("Erro ao carregar CNPJs da Flash:", error);
                return [];
            });
    }

    function enviarParaAssinatura(contratoId: number, cnpj: string): Promise<ContratoAssinaturaType | null> {
        setLoading(true);
        return api
            .post(`/Contrato/${contratoId}/enviar-assinatura?cnpj=${encodeURIComponent(cnpj)}`)
            .then((response) => {
                showToast("success", "Contrato enviado para assinatura.");
                setLoading(false);
                return response.data as ContratoAssinaturaType;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast(
                        "error",
                        error?.response?.data || "Erro ao enviar contrato para assinatura.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao enviar contrato para assinatura:", error);
                return null;
            });
    }

    function getStatusAssinatura(contratoId: number): Promise<ContratoAssinaturaType | null> {
        return api
            .get(`/Contrato/${contratoId}/assinatura`)
            .then((response) => response.data as ContratoAssinaturaType)
            .catch((error) => {
                if (error?.response?.status === 404) return null;
                if (error?.response?.status === 401) {
                    handleLogout();
                }
                console.error("Erro ao buscar status de assinatura do contrato:", error);
                return null;
            });
    }

    return {
        createContrato,
        enviarParaAssinatura,
        getStatusAssinatura,
        getFlashCnpjs,
        getAllContratoPaged,
        updateContrato,
        deleteContrato,
        setListContrato,
        listContrato,
        setPageCount,
        pageCount,
        setInitialListContrato,
        initialListContrato,
        setContrato,
        contrato,
        downloadContratoPdf,
        getContratoCursoGuidByCursoIdAndContratoId,
        updateStatusContrato,
        handleGetContratoByEmpresaId,
        getContratoById,
        suggestContratoDescriptions,
        replaceCursosDoContrato,
        addCursoAoContrato,
        removeCursoDoContrato,
    };
}